'use client';

import * as React from 'react';

import { cn } from '@/lib/utils';

export interface NumberStepperProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  className?: string;
  disabled?: boolean;
  variant?: 'orange' | 'purple' | 'blue' | 'green';
  /** Formats the displayed value, e.g. locale-aware decimals. */
  formatValue?: (value: number) => string;
}

const ACCENT_BUTTON: Record<NonNullable<NumberStepperProps['variant']>, string> = {
  orange: 'bg-orange-peel-500 hover:bg-orange-peel-600',
  purple: 'bg-purple-600 hover:bg-purple-700',
  blue: 'bg-blue-600 hover:bg-blue-700',
  green: 'bg-emerald-600 hover:bg-emerald-700',
};

/**
 * Bare stepper matching the Home cleaning panel: uppercase eyebrow label,
 * oversized numeral, bordered − / filled + controls. No card chrome.
 */
export const NumberStepper: React.FC<NumberStepperProps> = ({
  label,
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  unit,
  className,
  disabled = false,
  variant = 'orange',
  formatValue,
}) => {
  const display = (input: number) => (formatValue ? formatValue(input) : input.toString());

  const [isEditing, setIsEditing] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(() => display(value));
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Sync typed value when the external value changes (render-phase pattern).
  const [prevValue, setPrevValue] = React.useState(value);
  if (value !== prevValue && !isEditing) {
    setPrevValue(value);
    setInputValue(display(value));
  }

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDecrement = () => {
    // If value is below min (e.g. unset 0 with min 50), snap up to min rather than no-op.
    if (value < min) {
      onChange(min);
      return;
    }
    const newValue = Math.max(min, value - step);
    if (newValue !== value) {
      onChange(newValue);
    }
  };

  const handleIncrement = () => {
    // Jump to min when starting below the allowed range (common for office size defaults).
    if (value < min) {
      onChange(min);
      return;
    }
    const newValue = Math.min(max, value + step);
    if (newValue !== value) {
      onChange(newValue);
    }
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Allow numbers, optionally with a decimal part (steps can be fractional).
    if (newValue === '' || /^\d*([.,]\d{0,2})?$/.test(newValue)) {
      setInputValue(newValue);
    }
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    const numValue = Number.parseFloat(inputValue.replace(',', '.'));
    if (!Number.isNaN(numValue)) {
      const clampedValue = Math.max(min, Math.min(max, numValue));
      onChange(clampedValue);
      setInputValue(display(clampedValue));
    } else {
      setInputValue(display(value));
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setInputValue(display(value));
    }
  };

  // Allow + when below min so the first click can snap into range; keep − disabled until >= min.
  const isDecrementDisabled = disabled || value <= min;
  const isIncrementDisabled = disabled || (value >= max && value >= min);

  return (
    <div className={cn('flex flex-col items-center text-center', className)}>
      {label && (
        <p className='mb-3 text-xs font-bold uppercase tracking-[0.2em] text-oxford-blue/40'>
          {label}
        </p>
      )}
      <div className='flex items-center justify-center gap-4'>
        <button
          type='button'
          onClick={handleDecrement}
          disabled={isDecrementDisabled}
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-xl transition-colors',
            isDecrementDisabled
              ? 'cursor-not-allowed border-gray-200 text-gray-300'
              : 'border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700',
          )}
          aria-label={`Decrease ${label}`}
        >
          <span className='text-xl leading-none'>−</span>
        </button>
        <span className='min-w-[3rem] text-center text-4xl font-extrabold tabular-nums text-oxford-blue'>
          {isEditing ? (
            <input
              ref={inputRef}
              type='text'
              inputMode='numeric'
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              className='w-20 bg-transparent text-center text-4xl font-extrabold tabular-nums text-oxford-blue focus:outline-none'
              aria-label={`Edit ${label}`}
            />
          ) : (
            <button
              type='button'
              onClick={handleInputClick}
              disabled={disabled}
              aria-label={`Edit ${label}`}
              className={cn('cursor-pointer bg-transparent', disabled && 'cursor-not-allowed')}
            >
              {formatValue ? formatValue(value) : value}
              {unit && <span className='ml-1 text-sm text-oxford-blue/50'>{unit}</span>}
            </button>
          )}
        </span>
        <button
          type='button'
          onClick={handleIncrement}
          disabled={isIncrementDisabled}
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xl font-bold text-white transition-colors',
            isIncrementDisabled ? 'cursor-not-allowed bg-gray-200' : ACCENT_BUTTON[variant],
          )}
          aria-label={`Increase ${label}`}
        >
          <span className='text-xl font-bold leading-none'>+</span>
        </button>
      </div>
    </div>
  );
};
